import type { PipeCondition, PipeGrade } from '@/api/services/yard.api';

/**
 * How pipe reads on screen.
 *
 * The API stores grades as full API 5CT designations and conditions as enum
 * names; the yard says "L80" and "Class 2". These tables lived on the pipe tally
 * list, which left the tally's own page with nowhere to get them from.
 */

export const PIPE_GRADES: { value: PipeGrade; label: string }[] = [
  { value: 'API_5CT_J55', label: 'J55' },
  { value: 'API_5CT_K55', label: 'K55' },
  { value: 'API_5CT_L80', label: 'L80' },
  { value: 'API_5CT_N80', label: 'N80' },
  { value: 'API_5CT_C90', label: 'C90' },
  { value: 'API_5CT_T95', label: 'T95' },
  { value: 'API_5CT_P110', label: 'P110' },
  { value: 'API_5CT_Q125', label: 'Q125' },
  { value: 'OTHER', label: 'Other' },
];

export const PIPE_CONDITIONS: { value: PipeCondition; label: string }[] = [
  { value: 'NEW', label: 'New' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'CLASS_2', label: 'Class 2' },
  { value: 'CLASS_3', label: 'Class 3' },
  { value: 'SCRAP', label: 'Scrap' },
];

export const PIPE_TYPES = [
  { value: 'CASING', label: 'Casing' },
  { value: 'TUBING', label: 'Tubing' },
  { value: 'DRILL_PIPE', label: 'Drill Pipe' },
  { value: 'LINE_PIPE', label: 'Line Pipe' },
];

export const getGradeLabel = (grade: PipeGrade): string =>
  PIPE_GRADES.find((g) => g.value === grade)?.label ?? grade;

export const getPipeTypeLabel = (type: string): string =>
  PIPE_TYPES.find((t) => t.value === type)?.label ?? type;

/** Badge variant and label for a condition — scrap is the one that must stand out. */
export function pipeConditionBadge(condition: PipeCondition): {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  label: string;
} {
  const config: Record<
    PipeCondition,
    { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }
  > = {
    NEW: { variant: 'default', label: 'New' },
    PREMIUM: { variant: 'secondary', label: 'Premium' },
    CLASS_2: { variant: 'outline', label: 'Class 2' },
    CLASS_3: { variant: 'outline', label: 'Class 3' },
    SCRAP: { variant: 'destructive', label: 'Scrap' },
  };
  return config[condition] ?? { variant: 'secondary', label: condition };
}
